import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const FleetLanding = () => {
  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'pageview',
      page: '/fleet',
      audience: 'Fleet'
    });
  }, []);

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>URGAA for Fleet Operators - EV Fleet Management & Charging Optimization | Go4Garage</title>
        <meta name="title" content="URGAA for Fleet Operators - EV Fleet Management & Charging Optimization" />
        <meta name="description" content="Optimize your electric fleet operations with URGAA. Route optimization, charging cost analytics, predictive maintenance, and guaranteed charging station uptime. Reduce TCO by 30%." />
        <meta name="keywords" content="EV fleet management, electric fleet software, fleet charging optimization, EV fleet analytics, fleet charging stations, electric vehicle fleet India" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://urgaa.in/fleet" />
        <meta property="og:title" content="URGAA for Fleet Operators - EV Fleet Management & Charging" />
        <meta property="og:description" content="Optimize your electric fleet with route optimization, charging analytics, and guaranteed uptime." />
        <meta property="og:image" content="https://urgaa.in/images/og-fleet.jpg" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://urgaa.in/fleet" />
        <meta name="twitter:title" content="URGAA for Fleet Operators" />
        <meta name="twitter:description" content="EV fleet management and charging optimization platform" />
        <meta name="twitter:image" content="https://urgaa.in/images/twitter-fleet.jpg" />
        <meta name="twitter:site" content="@Go4Garage" />
        
        {/* Additional Meta */}
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://urgaa.in/fleet" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "URGAA for Fleet Operators",
            "applicationCategory": "BusinessApplication",
            "offers": {
              "@type": "Offer",
              "price": "500",
              "priceCurrency": "INR",
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": "500",
                "priceCurrency": "INR",
                "unitText": "per vehicle per month"
              }
            },
            "description": "EV fleet management and charging optimization platform",
            "provider": {
              "@type": "Organization",
              "name": "Go4Garage Private Limited",
              "url": "https://urgaa.in"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
        {/* Hero Section */}
        <section className="pt-20 pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-block px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-semibold mb-6">
              🚗 2,500+ Fleet Vehicles Managed | 30% TCO Reduction
            </div>
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Run Your Electric Fleet<br />Like a Pro
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Route optimization, charging cost analytics, predictive maintenance. Guaranteed charging station uptime with SLA credits.
            </p>
            <div className="flex gap-4 justify-center">
              <Link to="/login" className="px-8 py-4 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold">
                Start Free 30-Day Trial
              </Link>
              <a href="#demo" className="px-8 py-4 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 font-semibold">
                Watch Demo
              </a>
            </div>
            <p className="text-sm text-gray-500 mt-4">Works with any EV fleet | Integrates with your existing TMS</p>
          </div>
        </section>

        {/* Pain Points Section */}
        <section className="py-16 bg-red-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Why Fleet Electrification Projects Fail</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-red-600 text-4xl mb-4">⚡</div>
                <h3 className="text-xl font-bold mb-2">Charging Anxiety</h3>
                <p className="text-gray-600">"Will the charger work?" Unreliable public charging creates 2-3 hour delays.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-red-600 text-4xl mb-4">💸</div>
                <h3 className="text-xl font-bold mb-2">Hidden Costs</h3>
                <p className="text-gray-600">Charging during peak hours costs 3x more. No visibility into per-vehicle energy costs.</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="text-red-600 text-4xl mb-4">📍</div>
                <h3 className="text-xl font-bold mb-2">Suboptimal Routes</h3>
                <p className="text-gray-600">Routes designed for diesel. EVs need charging-aware route optimization.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Solution Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">URGAA Fleet Edition</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="border-2 border-green-500 p-6 rounded-lg">
                <div className="text-green-600 text-4xl mb-4">🗺️</div>
                <h3 className="text-xl font-bold mb-2">AI Route Optimization</h3>
                <p className="text-gray-600 mb-4">Charging-aware routes that minimize energy costs and maximize vehicle uptime</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Multi-stop optimization (20+ stops)</li>
                  <li>✓ Real-time rerouting if charger fails</li>
                  <li>✓ Battery degradation modeling</li>
                  <li>✓ 15-20% range improvement vs generic routing</li>
                </ul>
              </div>
              <div className="border-2 border-blue-500 p-6 rounded-lg">
                <div className="text-blue-600 text-4xl mb-4">💰</div>
                <h3 className="text-xl font-bold mb-2">Charging Cost Analytics</h3>
                <p className="text-gray-600 mb-4">Per-vehicle energy cost tracking with ToD (Time-of-Day) optimization</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Cost per km by vehicle</li>
                  <li>✓ Peak vs off-peak charging breakdown</li>
                  <li>✓ Automated ToD scheduling (save 40% on energy)</li>
                  <li>✓ Budget alerts & variance reporting</li>
                </ul>
              </div>
              <div className="border-2 border-purple-500 p-6 rounded-lg">
                <div className="text-purple-600 text-4xl mb-4">🔧</div>
                <h3 className="text-xl font-bold mb-2">Predictive Maintenance</h3>
                <p className="text-gray-600 mb-4">AI predicts battery degradation and maintenance needs before breakdowns</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ Battery health scoring (0-100)</li>
                  <li>✓ Predictive alerts 2 weeks in advance</li>
                  <li>✓ Maintenance cost forecasting</li>
                  <li>✓ 40% reduction in unplanned downtime</li>
                </ul>
              </div>
              <div className="border-2 border-indigo-500 p-6 rounded-lg">
                <div className="text-indigo-600 text-4xl mb-4">✅</div>
                <h3 className="text-xl font-bold mb-2">Guaranteed Uptime</h3>
                <p className="text-gray-600 mb-4">SLA-backed charging network access with automatic credit compensation</p>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li>✓ 98% uptime guarantee on partner chargers</li>
                  <li>✓ Auto-credits for downtime (₹500-2000/incident)</li>
                  <li>✓ Real-time charger health monitoring</li>
                  <li>✓ Backup charger recommendations</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Simple Fleet Pricing</h2>
            <div className="bg-white p-8 rounded-lg shadow-lg inline-block">
              <div className="text-5xl font-bold text-green-600 mb-2">₹500</div>
              <div className="text-gray-600 mb-6">per vehicle per month</div>
              <ul className="text-left space-y-3 mb-8">
                <li>✓ Unlimited users & drivers</li>
                <li>✓ Route optimization (unlimited runs)</li>
                <li>✓ Charging cost analytics</li>
                <li>✓ Predictive maintenance alerts</li>
                <li>✓ SLA-backed charging network access</li>
                <li>✓ Integration with your TMS/ERP</li>
              </ul>
              <Link to="/login" className="w-full block py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold">
                Start Free Trial
              </Link>
              <p className="text-sm text-gray-500 mt-4">Volume discounts available | 100+ vehicles: ₹350/vehicle</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-green-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-4">Ready to Optimize Your EV Fleet?</h2>
            <p className="text-xl mb-8">Join 200+ fleet operators saving 30% on TCO with URGAA</p>
            <Link to="/login" className="px-8 py-4 bg-white text-green-600 rounded-lg hover:bg-gray-100 font-semibold inline-block">
              Start Free 30-Day Trial →
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default FleetLanding;